import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Leaderboards = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('current');
  const [viewType, setViewType] = useState('seasonal'); // 'seasonal' or 'all-time'
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchSeasons();
    fetchCurrentSeason();
  }, []);

  useEffect(() => {
    if (selectedSeason === 'current' && currentSeason) {
      fetchSeasonalLeaderboard(currentSeason.id);
    } else if (selectedSeason !== 'current') {
      fetchSeasonalLeaderboard(selectedSeason);
    }
  }, [selectedSeason, currentSeason]);

  useEffect(() => {
    if (viewType === 'all-time') {
      fetchAllTimeLeaderboard();
    }
  }, [viewType]);

  const fetchSeasons = async () => {
    try {
      const response = await fetch('/api/seasons/all');
      const data = await response.json();
      
      if (data.success) {
        setSeasons(data.data);
      }
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchCurrentSeason = async () => {
    try {
      const response = await fetch('/api/seasons/current');
      const data = await response.json();
      
      if (data.success) {
        setCurrentSeason(data.data);
      }
    } catch (error) {
      console.error('Error fetching current season:', error);
    }
  };

  const fetchSeasonalLeaderboard = async (seasonId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/leaderboards/season/${seasonId}`);
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.data.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching seasonal leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimeLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/leaderboards/all-time');
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.data.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching all-time leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(`/api/leaderboards/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      
      if (data.success) {
        setSearchResults(data.data.heroes);
      }
    } catch (error) {
      console.error('Error searching heroes:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-400';
    if (rank === 2) return 'text-gray-300';
    if (rank === 3) return 'text-orange-400';
    if (rank <= 10) return 'text-green-400';
    return 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">Leaderboards</h1>
          <p className="text-gray-400">
            See how Heroes rank in seasonal and all-time competitions.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search Heroes..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
            {searchLoading && (
              <div className="absolute right-3 top-2.5 text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>

        {/* Search Results */}
        {searchQuery && searchResults.length > 0 && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-bold text-white mb-3">Search Results</h3>
            <div className="space-y-2">
              {searchResults.map(hero => (
                <Link
                  key={hero.id}
                  to={`/hero/${hero.id}`}
                  className="flex items-center justify-between p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <div>
                    <div className="text-white font-medium">{hero.name}</div>
                    <div className="text-gray-400 text-sm">by {hero.owner_username}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-yellow-400 font-bold">Level {hero.level}</div>
                    <div className="text-gray-400 text-sm">{(hero.total_xp || 0).toLocaleString()} XP</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* View Type Toggle */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-800 rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewType('seasonal')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                viewType === 'seasonal'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Seasonal
            </button>
            <button
              onClick={() => setViewType('all-time')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                viewType === 'all-time'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All-Time
            </button>
          </div>
        </div>

        {/* Season Selector (for seasonal view) */}
        {viewType === 'seasonal' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Season:
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="current">Current Season</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.status})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Leaderboard */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-white text-xl">Loading leaderboard...</div>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {viewType === 'seasonal' 
                  ? (currentSeason ? currentSeason.name : 'Seasonal Leaderboard')
                  : 'All-Time Leaderboard'
                }
              </h2>
              {viewType === 'seasonal' && currentSeason && (
                <p className="text-gray-400 text-sm mt-1">
                  {new Date(currentSeason.start_date).toLocaleDateString()} - {new Date(currentSeason.end_date).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Rank</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Hero</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Owner</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Level</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">
                      {viewType === 'seasonal' ? 'Season XP' : 'Total XP'}
                    </th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Wins</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Eliminations</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, index) => (
                    <tr key={entry.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="py-3 px-6">
                        <span className={`font-bold ${getRankColor(entry.rank)}`}>
                          {getRankIcon(entry.rank)}
                        </span>
                      </td>
                      <td className="py-3 px-6">
                        <Link
                          to={`/hero/${entry.id}`}
                          className="text-white font-medium hover:text-blue-400 transition-colors"
                        >
                          {entry.name}
                        </Link>
                      </td>
                      <td className="py-3 px-6 text-gray-300">
                        {entry.owner_username}
                      </td>
                      <td className="py-3 px-6 text-white">
                        {entry.level}
                      </td>
                      <td className="py-3 px-6 text-yellow-400 font-bold">
                        {((viewType === 'seasonal' ? entry.season_xp : entry.total_xp) || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-6 text-green-400">
                        {entry.wins || 0}
                      </td>
                      <td className="py-3 px-6 text-orange-400">
                        {entry.total_eliminations || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {leaderboard.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No heroes found for this leaderboard.
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">How Leaderboards Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-300">
            <div>
              <h3 className="font-bold text-white mb-2">Seasonal Rankings</h3>
              <p className="text-sm">
                Heroes compete in quarterly seasons. Rankings are based on XP earned during the current season.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">All-Time Rankings</h3>
              <p className="text-sm">
                Lifetime achievements across all seasons. Based on total XP earned throughout a hero's career.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">XP Sources</h3>
              <p className="text-sm">
                Heroes earn XP from battle participation, eliminations, placement bonuses, and wins.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">Token Holder Bonus</h3>
              <p className="text-sm">
                SOULWARS token holders receive a 1.5x XP multiplier in all battles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboards;
